import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Shield, Plus, UserPlus, AlertTriangle, Crown, Pencil, Trash2, X } from "lucide-react"
import { useAuth } from "../../hooks/useAuth"
import { Navigate } from "react-router-dom"
import { createTrainer, updateClient, deleteClient } from "../../api/clients"
import api from "../../api/client"
import { getInitials } from "../../utils/getInitials"

interface StaffUser {
  id: number
  name: string
  email: string
  role: string
  is_master: boolean
}

// ── Edit Trainer Modal ─────────────────────────────────────────────────────
function EditTrainerModal({
  trainer,
  onClose,
  onUpdated,
}: {
  trainer: StaffUser
  onClose: () => void
  onUpdated: (t: StaffUser) => void
}) {
  const [name, setName] = useState(trainer.name)
  const [email, setEmail] = useState(trainer.email)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await updateClient(trainer.id, { name, email })
      onUpdated({ ...trainer, name, email })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al actualizar el trainer.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Editar Trainer</h2>
            <p className="text-xs text-muted">{trainer.name}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white transition">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted mb-1 block">Nombre</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted mb-1 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
            />
          </div>

          {error && (
            <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-amber-400 py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── Delete Trainer Modal ───────────────────────────────────────────────────
function DeleteTrainerModal({
  trainer,
  onClose,
  onDeleted,
}: {
  trainer: StaffUser
  onClose: () => void
  onDeleted: () => void
}) {
  const [confirm, setConfirm] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isConfirmed = confirm.trim().toLowerCase() === trainer.name.trim().toLowerCase()

  async function handleDelete() {
    if (!isConfirmed) return
    setDeleting(true)
    try {
      await deleteClient(trainer.id)
      onDeleted()
    } catch {
      setError("No se pudo eliminar el trainer.")
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm rounded-2xl border border-danger/30 gradient-card p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger/10 border border-danger/20">
            <Trash2 size={16} className="text-danger" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Eliminar trainer</h2>
            <p className="text-xs text-muted mt-0.5">
              Esta acción es irreversible. Escribe el nombre para confirmar:{" "}
              <span className="font-semibold text-white">{trainer.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-muted hover:text-white transition shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Nombre del trainer"
          autoFocus
          className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-danger focus:outline-none mb-3"
        />

        {error && (
          <p className="mb-3 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleDelete}
            disabled={!isConfirmed || deleting}
            className="flex-1 rounded-lg bg-danger py-2.5 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-40"
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
          >
            Cancelar
          </button>
        </div>
      </motion.div>
    </div>
  )
}

// ── Main Admin Tab ─────────────────────────────────────────────────────────
export default function AdminTab() {
  const { isAdmin } = useAuth()
  const [staff, setStaff] = useState<StaffUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showNotice, setShowNotice] = useState(true)
  const [editingTrainer, setEditingTrainer] = useState<StaffUser | null>(null)
  const [deletingTrainer, setDeletingTrainer] = useState<StaffUser | null>(null)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    fetchStaff()
  }, [isAdmin])

  // Auto-dismiss notice after 2 seconds
  useEffect(() => {
    if (!showNotice) return
    const t = setTimeout(() => setShowNotice(false), 2000)
    return () => clearTimeout(t)
  }, [showNotice])

  if (!isAdmin) return <Navigate to="/dashboard/training" replace />

  async function fetchStaff() {
    try {
      const { data } = await api.get<StaffUser[]>("/clients", { params: { role: "staff" } })
      setStaff(Array.isArray(data) ? data : [])
    } catch {
      setStaff([])
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !email || !password) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await createTrainer({ name, email, password })
      setSuccess(`Trainer "${name}" creado exitosamente.`)
      setName("")
      setEmail("")
      setPassword("")
      setShowAdd(false)
      fetchStaff()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al crear el trainer.")
    } finally {
      setSaving(false)
    }
  }

  const masters = staff.filter((s) => s.is_master)
  const trainers = staff.filter((s) => !s.is_master)

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      {/* Modals */}
      {editingTrainer && (
        <EditTrainerModal
          trainer={editingTrainer}
          onClose={() => setEditingTrainer(null)}
          onUpdated={(updated) => {
            setStaff((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
            setEditingTrainer(null)
          }}
        />
      )}
      {deletingTrainer && (
        <DeleteTrainerModal
          trainer={deletingTrainer}
          onClose={() => setDeletingTrainer(null)}
          onDeleted={() => {
            setStaff((prev) => prev.filter((s) => s.id !== deletingTrainer.id))
            setDeletingTrainer(null)
          }}
        />
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 border border-amber-400/20">
            <Shield className="text-amber-400" size={20} />
          </div>
          <div>
            <h2
              className="text-xl font-bold tracking-wide text-white"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Panel de Administrador
            </h2>
            <p className="text-xs text-muted">Gestionar equipo de entrenadores</p>
          </div>
        </div>

        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition"
        >
          <UserPlus size={16} />
          Nuevo Trainer
        </button>
      </div>

      {/* Success message */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success"
          >
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Trainer Form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 gradient-card border border-amber-400/30 rounded-xl p-6"
          >
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <UserPlus size={16} className="text-amber-400" />
              Crear Nuevo Trainer
            </h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted mb-1 block">Nombre</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nombre completo"
                    required
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted mb-1 block">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="trainer@email.com"
                    required
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted mb-1 block">Contraseña</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="min. 8 caracteres"
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none"
                  />
                </div>
              </div>

              {error && (
                <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  {error}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
                >
                  <Plus size={14} />
                  {saving ? "Creando..." : "Crear Trainer"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false)
                    setError(null)
                  }}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm text-muted hover:text-white transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info notice — auto-dismisses after 2s */}
      <AnimatePresence>
        {showNotice && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="mb-6 flex items-start gap-3 rounded-lg border border-amber-400/20 bg-amber-400/5 px-4 py-3"
          >
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-muted">
              Solo tu cuenta de administrador puede crear nuevos entrenadores. El registro público
              está deshabilitado — los clientes se crean desde “Entrenamiento”.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Master Admin Cards */}
          {masters.length > 0 && (
            <div>
              <h3
                className="mb-4 text-sm font-semibold text-white uppercase tracking-widest"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Administradores Master
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {masters.map((master, i) => (
                  <motion.div
                    key={master.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="gradient-card border border-amber-400/40 rounded-xl p-5 relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
                    <div className="flex items-center gap-4 relative">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/20 border-2 border-amber-400/50 flex items-center justify-center text-amber-400 text-lg font-bold shrink-0 relative">
                        {getInitials(master.name)}
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                          <Crown size={10} className="text-black" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-base font-bold text-white">{master.name}</p>
                          <span className="rounded-full bg-amber-400/20 border border-amber-400/30 px-2.5 py-0.5 text-[10px] font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1">
                            <Crown size={8} />
                            Master
                          </span>
                        </div>
                        <p className="text-xs text-muted mt-0.5">{master.email}</p>
                      </div>
                      <span className="rounded-full bg-amber-400/10 border border-amber-400/20 px-3 py-1 text-xs font-medium text-amber-400 shrink-0">
                        Admin
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Trainers List */}
          <div>
            <h3
              className="mb-4 text-sm font-semibold text-white uppercase tracking-widest"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Entrenadores ({trainers.length})
            </h3>

            {trainers.length === 0 ? (
              <div className="gradient-card border border-white/10 rounded-xl p-12 text-center">
                <Shield size={48} className="mx-auto mb-4 text-muted opacity-30" />
                <p className="text-muted text-sm">No hay trainers registrados aún.</p>
                <p className="text-xs text-muted/60 mt-1">
                  Usa el botón “Nuevo Trainer” para agregar uno.
                </p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {trainers.map((t, i) => (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="gradient-card border border-white/10 rounded-xl p-4 hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-sm font-bold shrink-0">
                        {getInitials(t.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">{t.name}</p>
                        <p className="text-xs text-muted truncate">{t.email}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setEditingTrainer(t)}
                          className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-primary/10 transition"
                          title="Editar"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => setDeletingTrainer(t)}
                          className="p-1.5 rounded-lg text-muted hover:text-danger hover:bg-danger/10 transition"
                          title="Eliminar"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  )
}
